/**
 * Types and pure helpers for the reusable-assessment feature.
 *
 * An Assessment is a named, purpose-tagged form — workshop pre-work, a feedback form, a survey —
 * generalized out of the hardcoded workshop question set. It is a tree:
 *
 *   Assessment → Section[] → Question[]
 *
 * and it is filled in as:
 *
 *   ResponseSet → Response[] (one per answered question)
 *
 * Everything here is backed by generic toolbox_db CRUD (`db/assessment`, `db/assessment-section`,
 * `db/assessment-question`, `db/assessment-response-set`, `db/assessment-response`), so the shapes
 * mirror `db/models/assessment.py` exactly. Foreign keys serialize as their integer pk, the way the
 * GTM content tables do.
 *
 * The helpers are deliberately pure (no fetching, no React) so both the builder and the fill-in
 * surface can share them and so `isQuestionVisible` can be unit-tested against the same
 * `visible_when` rules the backend stores.
 */

// ---------------------------------------------------------------------------
// Enums, mirrored from db/models/assessment.py
// ---------------------------------------------------------------------------

/** What an assessment is for. Loose classification, used for filtering and grouping only. */
export type AssessmentPurpose =
  | 'workshop_prework'
  | 'feedback'
  | 'survey'
  | 'discovery'
  | 'general';

export const ASSESSMENT_PURPOSES: { value: AssessmentPurpose; label: string }[] = [
  { value: 'workshop_prework', label: 'Workshop Pre-work' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'survey', label: 'Survey' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'general', label: 'General' },
];

/** How a single question is answered. Drives which input renders and how the value is stored. */
export type AssessmentResponseType =
  | 'text'
  | 'textarea'
  | 'boolean'
  | 'scale'
  | 'single_choice'
  | 'multi_choice'
  | 'number'
  | 'date'
  | 'file'
  | 'document_upload'
  | 'sf_account_file'
  | 'sf_account'
  | 'sf_opportunity'
  | 'data_import';

export const RESPONSE_TYPES: { value: AssessmentResponseType; label: string }[] = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'scale', label: 'Scale' },
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multi_choice', label: 'Multiple Choice' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'file', label: 'File Reference (link)' },
  { value: 'document_upload', label: 'Document Upload' },
  { value: 'sf_account_file', label: 'Salesforce Account File' },
  { value: 'sf_account', label: 'Salesforce Account' },
  { value: 'sf_opportunity', label: 'Salesforce Opportunity' },
  { value: 'data_import', label: 'Data Import (Excel/CSV → widgets)' },
];

/** The response types that carry a list of options. */
export const CHOICE_RESPONSE_TYPES: AssessmentResponseType[] = ['single_choice', 'multi_choice'];

/** Response types answered by uploading a file rather than typing. */
export const FILE_RESPONSE_TYPES: AssessmentResponseType[] = ['document_upload', 'sf_account_file'];

/** Response types answered by picking a Salesforce record (by name or id). */
export const SF_LOOKUP_RESPONSE_TYPES: AssessmentResponseType[] = ['sf_account', 'sf_opportunity'];

/**
 * Response types whose inputs perform real side effects on interaction (network
 * calls, uploads, or dataset/widget creation). Previews render these disabled so
 * an admin looking at a preview can't accidentally create datasets or hit
 * Salesforce. See `AssessmentQuestionInput` and the layout `previewMode` flag.
 */
export const PREVIEW_UNSAFE_RESPONSE_TYPES: AssessmentResponseType[] = [
  'document_upload',
  'sf_account_file',
  'sf_account',
  'sf_opportunity',
  'data_import',
];

export type AssessmentResponseSetStatus = 'in_progress' | 'submitted' | 'reviewed';

// ---------------------------------------------------------------------------
// Record shapes
// ---------------------------------------------------------------------------

export interface AssessmentQuestionOption {
  value: string;
  label: string;
}

/**
 * A rule that hides a question until another answer is given, stored on the question/section as
 * `visible_when`. `null`/absent means always visible. Kept small on purpose: an answer key plus one
 * of a few comparisons, evaluated by `isConditionMet` below.
 */
export interface VisibleWhen {
  /** The `key` of the question whose answer this depends on. */
  question: string;
  /** Show when the referenced answer equals this. */
  equals?: string | number | boolean;
  /** Show when the referenced answer is one of these (for multi-choice sources). */
  in?: (string | number)[];
  /** Show when the referenced question has any answer at all. */
  notEmpty?: boolean;
}

export interface Assessment {
  id: number;
  key: string;
  name: string;
  description: string;
  purpose: AssessmentPurpose;
  /** Loose link to what the assessment is about (e.g. 'workshop', 'customer'); not an FK. */
  subject_type: string | null;
  is_published: boolean;
  allow_anonymous: boolean;
  is_active: boolean;
  metadata: Record<string, any>;
  created?: string;
  last_updated?: string;
  updated_by?: string;
}

export interface AssessmentSection {
  id: number;
  assessment: number;
  key: string;
  title: string;
  description: string;
  order: number;
  visible_when: VisibleWhen | null;
  active: boolean;
}

export interface AssessmentQuestion {
  id: number;
  section: number;
  key: string;
  text: string;
  help_text: string;
  order: number;
  response_type: AssessmentResponseType;
  response_options: any;
  visible_when: VisibleWhen | null;
  weight: string | number | null;
  required: boolean;
  active: boolean;
}

/**
 * Answer shape for `sf_account` / `sf_opportunity` — a picked Salesforce record.
 *
 * The canonical definition now lives in `support/salesforce.ts` (the campaign-first home for
 * Salesforce linkage). Re-exported here so existing `import { SalesforceRef } from './assessments'`
 * sites keep working.
 */
export type { SalesforceRef } from './salesforce';

/** Answer shape for `document_upload` — a file stored as a generic resource file (S3). */
export interface DocumentUploadAnswer {
  resource_id: number;
  file_name: string;
}

/** Answer shape for `sf_account_file` — a file stored in Salesforce against an Account. */
export interface SalesforceFileAnswer {
  account_id: string;
  content_document_id: string;
  content_version_id: string;
  file_name: string;
}

/**
 * Answer shape for `data_import` — an Excel/CSV file parsed into one or more widgets. The answer keeps
 * only a reference to the persisted `ImportedDataset` (+ the `CustomTile`s built from it); the parsed
 * rows and widget data live in that record, so the report and dashboards re-render without re-parsing.
 */
export interface DataImportAnswer {
  datasetId: number;
  presetKey?: string;
  tileIds: number[];
  widgetCount: number;
  /**
   * When the question is coupled to an `sf_account` answer, the raw uploaded file is also stored as
   * a Salesforce File on that account (best-effort — absent if there was no account or the upload
   * failed). The parsed widgets above are unaffected either way.
   */
  salesforceFile?: SalesforceFileAnswer;
}

export interface AssessmentResponseSet {
  id: number;
  assessment: number;
  subject_type: string | null;
  subject_id: string | null;
  respondent: string | null;
  status: AssessmentResponseSetStatus;
  submitted_at: string | null;
  score: string | number | null;
  metadata: Record<string, any>;
  // Read-only, added by AssessmentResponseSetSerializer.
  assessment_name?: string;
  assessment_purpose?: AssessmentPurpose;
  created?: string;
  last_updated?: string;
}

export interface AssessmentResponse {
  id: number;
  response_set: number;
  question: number;
  response: any;
  score: string | number | null;
  // Read-only, added by AssessmentResponseSerializer.
  question_key?: string;
  question_text?: string;
  question_response_type?: AssessmentResponseType;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

const purposeLabels = new Map(ASSESSMENT_PURPOSES.map((p) => [p.value, p.label]));
const responseTypeLabels = new Map(RESPONSE_TYPES.map((t) => [t.value, t.label]));

export const purposeLabel = (purpose?: string): string =>
  (purpose && purposeLabels.get(purpose as AssessmentPurpose)) || purpose || '—';

export const responseTypeLabel = (type?: string): string =>
  (type && responseTypeLabels.get(type as AssessmentResponseType)) || type || '—';

/** Sort by `order`, breaking ties by id, matching the backend's `('order', 'id')` ordering. */
export const byOrder = <T extends { order?: number; id?: number }>(rows: T[]): T[] =>
  [...(rows || [])].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0) || (a?.id ?? 0) - (b?.id ?? 0));

/**
 * Normalize the free-form `response_options` JSON into `{value, label}[]`.
 *
 * The column is a JSONField so it may hold an array of strings, an array of `{value, label}`, or
 * be empty. This flattens all three so the renderers never have to branch on shape.
 */
export const normalizeOptions = (raw: any): AssessmentQuestionOption[] => {
  const source = Array.isArray(raw) ? raw : Array.isArray(raw?.options) ? raw.options : [];
  return source
    .map((option: any) => {
      if (option && typeof option === 'object') {
        const value = option.value ?? option.key ?? option.id ?? option.label;
        if (value === undefined || value === null) return null;
        return { value: String(value), label: String(option.label ?? value) };
      }
      if (option === undefined || option === null) return null;
      return { value: String(option), label: String(option) };
    })
    .filter(Boolean) as AssessmentQuestionOption[];
};

/** The blank answer for a question, so the form has a controlled value from the first render. */
export const emptyAnswer = (type: AssessmentResponseType): any => {
  switch (type) {
    case 'multi_choice':
      return [];
    case 'scale':
    case 'number':
      return '';
    // Boolean blanks to null (not false) so an untouched toggle reads as unanswered; only an
    // explicit Yes/No tap yields a real boolean. The tri-state toggle input already renders
    // null as "nothing selected". Upload and Salesforce-lookup answers are likewise objects
    // once given, so they blank to null too — `hasAnswer` reads all of these as unanswered
    // until the user actually interacts.
    case 'boolean':
    case 'document_upload':
    case 'sf_account_file':
    case 'sf_account':
    case 'sf_opportunity':
    case 'data_import':
      return null;
    default:
      return '';
  }
};

/**
 * A representative sample answer for a question, used to render live previews
 * in the assessment builder (how a question looks at fill time) without any
 * real submission. Self-contained types get a realistic value; the
 * side-effecting types (uploads, Salesforce lookups, data import) return null —
 * a preview renders those disabled so no network/upload/dataset writes fire.
 */
export const demoAnswer = (question: AssessmentQuestion): any => {
  const type = question.response_type;
  switch (type) {
    case 'multi_choice': {
      const opts = normalizeOptions(question.response_options);
      return opts.slice(0, 2).map((o) => o.value);
    }
    case 'single_choice': {
      const opts = normalizeOptions(question.response_options);
      return opts[0]?.value ?? '';
    }
    case 'scale': {
      const raw = question.response_options ?? {};
      const min = Number.isFinite(Number(raw.min)) ? Number(raw.min) : 1;
      const max = Number.isFinite(Number(raw.max)) ? Number(raw.max) : 5;
      return Math.round((min + max) / 2);
    }
    case 'boolean':
      return true;
    case 'number':
      return 42;
    case 'date':
      return new Date().toISOString().slice(0, 10);
    case 'textarea':
      return 'PseudoCo is standardizing on Cisco security across cloud, campus, and AI workloads.';
    case 'file':
      return 'example-attachment.pdf';
    case 'text':
      return 'Sample response';
    // Side-effecting inputs: no demo value; previews render these disabled.
    case 'document_upload':
    case 'sf_account_file':
    case 'sf_account':
    case 'sf_opportunity':
    case 'data_import':
      return null;
    default:
      return 'Sample response';
  }
};

/** True when an answer counts as "given" — used for `required` validation and `notEmpty` rules. */
export const hasAnswer = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return true;
  return true;
};

const valuesEqual = (a: any, b: any): boolean => {
  if (typeof a === 'boolean' || typeof b === 'boolean') return Boolean(a) === Boolean(b);
  return String(a) === String(b);
};

/** Evaluate one `visible_when` rule against the current answers, keyed by question key. */
export const isConditionMet = (
  rule: VisibleWhen | null | undefined,
  answersByKey: Record<string, any>
): boolean => {
  if (!rule || !rule.question) return true;
  const answer = answersByKey[rule.question];

  if (rule.notEmpty) return hasAnswer(answer);

  if (rule.in && Array.isArray(rule.in)) {
    if (Array.isArray(answer)) return answer.some((a) => rule.in!.some((v) => valuesEqual(a, v)));
    return rule.in.some((v) => valuesEqual(answer, v));
  }

  if (rule.equals !== undefined) {
    if (Array.isArray(answer)) return answer.some((a) => valuesEqual(a, rule.equals));
    return valuesEqual(answer, rule.equals);
  }

  return true;
};

/** Whether a question should render, given the section is visible and its own rule holds. */
export const isQuestionVisible = (
  question: AssessmentQuestion,
  answersByKey: Record<string, any>
): boolean => question.active !== false && isConditionMet(question.visible_when, answersByKey);

export const isSectionVisible = (
  section: AssessmentSection,
  answersByKey: Record<string, any>
): boolean => section.active !== false && isConditionMet(section.visible_when, answersByKey);

/** A stable, human-ish key from a title/name, for pre-filling the `key` field in the builder. */
export const slugifyKey = (input: string): string =>
  (input || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);

/**
 * A unique, auto-generated key for a new section or question.
 *
 * Keys are no longer author-entered — the builder assigns one at creation from the title/text and
 * never changes it again, so the `visible_when` rules that reference a question by key stay valid
 * across edits. `taken` is the set of sibling keys to avoid; a numeric suffix is appended on
 * collision, falling back to a timestamp if the title is empty or every suffix is somehow taken.
 */
export const uniqueKey = (base: string, taken: Iterable<string>): string => {
  const used = new Set(taken);
  const root = slugifyKey(base) || `item-${Date.now().toString(36)}`;
  if (!used.has(root)) return root;
  for (let i = 2; i < 1000; i += 1) {
    const suffix = `-${i}`;
    const candidate = `${root.slice(0, 100 - suffix.length)}${suffix}`;
    if (!used.has(candidate)) return candidate;
  }
  const suffix = `-${Date.now().toString(36)}`;
  return `${root.slice(0, 100 - suffix.length)}${suffix}`;
};

/**
 * Reorder a copy of `list` so the item at `from` lands at `to` — the array move a drag-and-drop
 * drop performs. Pure; callers persist the new `order` (the item's index) afterward.
 */
export const arrayMove = <T>(list: T[], from: number, to: number): T[] => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

// ---------------------------------------------------------------------------
// Salesforce account-file coupling
// ---------------------------------------------------------------------------

/**
 * The questions in the assessment that pick a Salesforce Account. A `sf_account_file` question
 * attaches its upload to the account chosen in one of these, so it can only work when at least one
 * exists — and must say which one when there is more than one.
 */
export const accountLookupQuestions = (allQuestions: AssessmentQuestion[]): AssessmentQuestion[] =>
  (allQuestions || []).filter((q) => q.response_type === 'sf_account');

/**
 * Resolve which `sf_account` question a `sf_account_file` question uploads against.
 *
 * Prefers the explicit `response_options.accountSource` key. Falls back to the only account
 * question when there is exactly one (so a single-account assessment needs no configuration).
 * Returns null when the author must still choose — no account question, or several and none picked.
 */
export const resolveAccountSourceKey = (
  question: AssessmentQuestion,
  allQuestions: AssessmentQuestion[]
): string | null => {
  const configured = (question.response_options as any)?.accountSource;
  const sources = accountLookupQuestions(allQuestions);
  if (configured && sources.some((q) => q.key === configured)) return configured;
  if (sources.length === 1) return sources[0].key;
  return null;
};
